import type {
  BackupAdapterKind,
  BackupDestinationHandle,
  BackupDestinationProvider,
  BackupWriteResult,
  ContactPickerProvider,
  OnDeviceAIProvider,
  PickedContact,
  PickedFile,
  PurchaseOutcome,
  PurchaseProvider,
  PurchaseProduct,
  ScanProvider,
  SaveBytesOptions,
} from '../types.js'

/**
 * Remaining web providers. Optional capabilities report availability and never
 * perform I/O when unavailable; each has a deterministic fallback (PRD §10.1).
 */

export interface WebScanEnvironment {
  /** Camera capture pipeline lands in S1.1; until then Scan is disabled. */
  readonly capture?: () => Promise<readonly PickedFile[]>
  readonly available?: boolean
}

export function createWebScanProvider(env: WebScanEnvironment = {}): ScanProvider {
  const available = env.available ?? env.capture !== undefined
  return {
    available,
    async scan(): Promise<readonly PickedFile[]> {
      if (!available || env.capture === undefined) return []
      return env.capture()
    },
  }
}

export interface WebContactPickerEnvironment {
  readonly select_contacts?: () => Promise<readonly PickedContact[]>
}

export function createWebContactPickerProvider(
  env: WebContactPickerEnvironment = {},
): ContactPickerProvider {
  const available = env.select_contacts !== undefined
  return {
    available,
    async pickContact(): Promise<PickedContact | null> {
      if (!available || env.select_contacts === undefined) return null
      const contacts = await env.select_contacts()
      return contacts[0] ?? null
    },
  }
}

export function createUnavailablePurchaseProvider(): PurchaseProvider {
  return {
    provider_id: 'unavailable',
    async listProducts(_productIds: readonly string[]): Promise<readonly PurchaseProduct[]> {
      return []
    },
    async purchase(): Promise<PurchaseOutcome> {
      return { status: 'unavailable' }
    },
  }
}

export interface WebBackupEnvironment {
  readonly adapters?: readonly BackupAdapterKind[]
  readonly choose_destination?: () => Promise<BackupDestinationHandle | null>
  readonly write_file?: (
    destination: BackupDestinationHandle,
    file_name: string,
    bytes: Uint8Array,
  ) => Promise<BackupWriteResult>
  readonly download?: (options: SaveBytesOptions) => void
}

export function createWebBackupDestinationProvider(
  env: WebBackupEnvironment,
): BackupDestinationProvider {
  const adapters = env.adapters ?? ['download']
  return {
    adapters,
    async chooseDestination(): Promise<BackupDestinationHandle | null> {
      if (env.choose_destination === undefined) return null
      return env.choose_destination()
    },
    async writeSnapshot(
      destination: BackupDestinationHandle,
      file_name: string,
      bytes: Uint8Array,
    ): Promise<BackupWriteResult> {
      if (env.write_file !== undefined) {
        return env.write_file(destination, file_name, bytes)
      }
      if (adapters.includes('download') && env.download !== undefined) {
        env.download({ suggested_name: file_name, media_type: 'application/octet-stream', bytes })
        // A download cannot confirm a write: report initiated, never verified.
        return { status: 'initiated' }
      }
      return { status: 'failed', error_code: 'OP_BACKUP_DESTINATION_UNAVAILABLE' }
    },
  }
}

export function createWebOnDeviceAIProvider(adapterId: string | null): OnDeviceAIProvider {
  return { adapter_id: adapterId ?? 'none', available: adapterId !== null }
}
